import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CaretRight,
  CaretDown,
  Translate,
  ArrowLeft,
  Gear,
  EnvelopeSimple,
  Gavel,
  Bell,
  Buildings,
  Shield,
  Elevator,
  Wrench,
  FireExtinguisher,
  Tag,
  Warning,
  ListChecks,
} from '@phosphor-icons/react';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { EmailSettingsSection } from '../pages/Settings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Phase 207e/207g — Centralized "Settings" hub inside Manage Buildings.
 *
 * Hosts every setting that applies to ALL buildings (not per-building, not
 * organization-wide). Sections rendered as a clickable list; clicking a row
 * drills into that section. NOT a modal, NOT a separate route.
 *
 * Sections:
 *   - Email Settings   (sender, Brevo key, email templates)
 *   - Meeting Templates (legal-quorum & committee templates)
 *   - Notifications & Reminders (placeholder — coming soon)
 */
export default function BuildingsSettingsPanel({ isAdmin, pageMode = false }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  // Phase 350 — In `pageMode` the panel is rendered as a standalone page,
  // so the collapsible drawer is hidden and the section list is always
  // visible.
  const [open, setOpen] = useState(pageMode);
  const [activeSection, setActiveSection] = useState(null);

  if (!isAdmin) return null;

  const sections = [
    {
      id: 'emailSettings',
      icon: EnvelopeSimple,
      label: 'Email Settings',
      desc: 'Αποστολή πρακτικών, notifications, sender · Brevo integration',
    },
    {
      // Phase 349 — Global Email Templates editor (was previously embedded
      // inside each building's "Email Templates" tab). Curating templates
      // centrally lets every building pick from the same library.
      id: 'emailTemplates',
      icon: EnvelopeSimple,
      label: t('emailTemplates') || 'Email Templates',
      desc: t('emailTemplatesCentralHint') || 'Κεντρική διαχείριση προτύπων email — κοινά για όλες τις πολυκατοικίες.',
      path: '/email-templates',
    },
    {
      // Phase 346 — Companies hub. Centralizes the 4 service-provider
      // catalogues (Insurance / Elevator / Certification / Fire) so admins
      // can manage every vendor from one Settings entry instead of hunting
      // through different top-bar menus.
      id: 'companies',
      icon: Buildings,
      label: t('companies') || 'Companies',
      desc: t('companiesHint') || 'Διαχείριση εταιρειών παρόχων (ασφάλεια, ανελκυστήρας, πιστοποίηση, πυροσβεστήρες)',
    },
    {
      // Phase 348 — Vendors moved out of per-building menus and into Settings
      // so the supplier list is curated once and reused everywhere.
      id: 'vendors',
      icon: Wrench,
      label: t('vendors') || 'Vendors',
      desc: t('vendorsCentralHint') || 'Κεντρικός κατάλογος προμηθευτών — προσθήκη, επεξεργασία, διαγραφή και αναζήτηση.',
      path: '/vendors',
    },
    {
      // Phase 348 — Expense Categories moved out of per-building menus.
      // Category codes are shared across every building so curating them
      // centrally keeps reports consistent.
      id: 'expenseCategories',
      icon: Tag,
      label: t('expenseCategories') || 'Expense Categories',
      desc: t('expenseCategoriesCentralHint') || 'Κεντρική διαχείριση κατηγοριών εξόδων (μηνιαία + κεφαλαιουχικά) — ισχύει σε όλες τις πολυκατοικίες.',
      path: '/expense-categories',
    },
    {
      // Phase 348 — Failure Reasons (elevator). Same vocabulary across every
      // building — manage once, used everywhere.
      id: 'failureReasons',
      icon: Warning,
      label: t('failureReasons') || 'Failure Reasons',
      desc: t('failureReasonsCentralHint') || 'Κεντρική λίστα αιτιών βλαβών ανελκυστήρα — κοινή για όλες τις πολυκατοικίες.',
      path: '/failure-reasons',
    },
    {
      // Phase 352 — General Meetings hub. Contains the two assembly-related
      // template catalogues: Agenda Templates (the topic checklist used when
      // creating a meeting) and Meeting Text Templates (quorum & decision
      // legal text). Both apply to every building so they live centrally.
      id: 'generalMeetings',
      icon: Gavel,
      label: t('generalMeetings') || 'General Meetings',
      desc: t('generalMeetingsHint') || 'Πρότυπα Ημερήσιας Διάταξης + Πρότυπα Κειμένων Συνελεύσεων — κοινά για όλες τις πολυκατοικίες.',
    },
    {
      id: 'notifications',
      icon: Bell,
      label: 'Notifications & Reminders',
      desc: 'Αυτόματες υπενθυμίσεις πληρωμών, λήξεων, συνελεύσεων',
      comingSoon: true,
    },
  ];

  const handleToggle = () => {
    setOpen((o) => !o);
    setActiveSection(null);
  };

  return (
    <div className={pageMode ? "bg-white border border-zinc-200" : "bg-white border border-zinc-200 mb-4"} data-testid="buildings-settings-panel">
      {!pageMode && (
        <button
          type="button"
          onClick={handleToggle}
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors"
          data-testid="buildings-settings-toggle"
        >
          <Gear size={16} weight="fill" className="text-zinc-600" />
          <div className="flex-1 text-left">
            <h2 className="font-bold text-sm" style={{ fontFamily: 'var(--font-heading)' }}>Settings</h2>
            <p className="text-[11px] text-zinc-500 leading-none mt-0.5">Κοινά settings που ισχύουν σε όλες τις πολυκατοικίες</p>
          </div>
          <CaretRight size={14} className={`text-zinc-400 transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>
      )}

      {open && (
        <div className={pageMode ? "" : "border-t border-zinc-100"} data-testid="buildings-settings-body">
          {!activeSection ? (
            // Section list view
            <div className="divide-y divide-zinc-100" data-testid="buildings-settings-section-list">
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  type="button"
                  onClick={() => {
                    if (sec.comingSoon) return;
                    // Phase 348 — Sections with a `path` deep-link to a
                    // dedicated CRUD page (Vendors / Expense Categories /
                    // Failure Reasons) so we don't duplicate huge tables
                    // inside the Settings drawer.
                    if (sec.path) { navigate(sec.path); return; }
                    setActiveSection(sec.id);
                  }}
                  disabled={sec.comingSoon}
                  className={`w-full px-5 py-4 flex items-center gap-4 text-left transition-colors ${
                    sec.comingSoon ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-50'
                  }`}
                  data-testid={`bld-settings-section-${sec.id}`}
                >
                  <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <sec.icon size={20} weight="fill" className="text-zinc-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-zinc-800">{sec.label}</p>
                      {sec.comingSoon && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-400 border border-zinc-200">
                          {t('comingSoon') || 'Coming Soon'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-400 mt-0.5">{sec.desc}</p>
                  </div>
                  {!sec.comingSoon && <CaretRight size={16} className="text-zinc-300 flex-shrink-0" />}
                </button>
              ))}
            </div>
          ) : (
            // Drilled-in section view
            <div className="px-5 py-4">
              <button
                type="button"
                onClick={() => setActiveSection(null)}
                className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4"
                data-testid="bld-settings-back-btn"
              >
                <ArrowLeft size={14} />
                <span className="font-semibold">{t('settings') || 'Settings'}</span>
              </button>
              {activeSection === 'emailSettings' && (
                <EmailSettingsSection t={t} isAdmin={isAdmin} />
              )}
              {activeSection === 'companies' && (
                <CompaniesSection t={t} />
              )}
              {activeSection === 'generalMeetings' && (
                <GeneralMeetingsSection t={t} isAdmin={isAdmin} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Meeting Templates inline editor (extracted from earlier inline implementation)
// ---------------------------------------------------------------------------
// Phase 734 — Simplified. `Restore default` buttons removed (texts are
// admin-managed templates, not system defaults). Each section shows the
// Greek textarea by default with an "English Translation" accordion
// directly underneath (collapsed by default, remembers its state while
// editing). Save/read/PDF logic is untouched.
function MeetingTemplateSection({ t, title, greekLabel, greekValue, onGreekChange, greekRows = 4, greekMono = false, englishLabel, englishValue, onEnglishChange, englishRows = 4, englishMono = false, englishHelp, testidPrefix, disabled }) {
  const [enExpanded, setEnExpanded] = useState(false);
  const hasEnglish = (englishValue || '').trim().length > 0;
  return (
    <div data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-semibold">{title}</Label>
      </div>
      <Textarea
        value={greekValue}
        onChange={(e) => onGreekChange(e.target.value)}
        rows={greekRows}
        className={`rounded-none text-sm ${greekMono ? 'font-mono' : ''}`}
        data-testid={`${testidPrefix}-input-inline`}
        disabled={disabled}
        aria-label={greekLabel}
      />
      <button
        type="button"
        onClick={() => setEnExpanded((v) => !v)}
        className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 font-semibold border-t border-zinc-100 pt-2 w-full mt-1"
        data-testid={`${testidPrefix}-en-toggle`}
      >
        {enExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <Translate size={12} />
        <span>{englishLabel}</span>
        {hasEnglish && (
          <span className="ml-1 inline-block text-[9px] px-1 border border-emerald-300 bg-emerald-50 text-emerald-700 uppercase">
            Filled
          </span>
        )}
      </button>
      {enExpanded && (
        <div className="mt-2">
          {englishHelp && (
            <p className="text-[11px] text-zinc-500 mb-1">{englishHelp}</p>
          )}
          <Textarea
            value={englishValue}
            onChange={(e) => onEnglishChange(e.target.value)}
            rows={englishRows}
            className={`rounded-none text-sm ${englishMono ? 'font-mono' : ''}`}
            data-testid={`${testidPrefix}-en-input-inline`}
            disabled={disabled}
            aria-label={englishLabel}
          />
        </div>
      )}
    </div>
  );
}

function MeetingTemplatesSection({ isAdmin }) {
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    legal_text: '',
    legal_text_en: '',
    committee_text: '',
    committee_text_en: '',
    epilogue_text_el: '',
    epilogue_text_en: '',
  });

  useEffect(() => {
    axios
      .get(`${API}/meeting-text-templates`)
      .then((r) => {
        setForm({
          legal_text: r.data?.legal_text || '',
          legal_text_en: r.data?.legal_text_en || '',
          committee_text: r.data?.committee_text || '',
          committee_text_en: r.data?.committee_text_en || '',
          epilogue_text_el: r.data?.epilogue_text_el || '',
          epilogue_text_en: r.data?.epilogue_text_en || '',
        });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/meeting-text-templates`, form);
      toast.success('Αποθηκεύτηκε');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="text-xs text-zinc-400 py-4 text-center">Φόρτωση…</div>;

  return (
    <div className="space-y-5" data-testid="meeting-templates-section">
      <p className="text-xs text-zinc-500">
        Τα παρακάτω κείμενα ισχύουν σε όλες τις συνελεύσεις. Σε επίπεδο συγκεκριμένης συνέλευσης μπορείτε να κάνετε override.
      </p>
      <div className="bg-blue-50 border border-blue-200 px-3 py-2 text-[11px] text-blue-900">
        <strong>Διαθέσιμες μεταβλητές</strong> (αντικαθίστανται αυτόματα κατά την έκδοση PDF):
        <code className="ml-2 px-1 bg-white border border-blue-200">{'{company_name}'}</code>
        <code className="ml-1 px-1 bg-white border border-blue-200">{'{company_email}'}</code>
        <code className="ml-1 px-1 bg-white border border-blue-200">{'{company_phone}'}</code>
        <span className="ml-2 text-blue-700">— οι τιμές διαβάζονται από τις Ρυθμίσεις Εταιρίας, οπότε αν αλλάξετε τα στοιχεία, όλα τα νέα PDF ενημερώνονται αυτόματα.</span>
      </div>

      <MeetingTemplateSection
        title="Νομικό Κείμενο Απαρτίας"
        greekLabel="Νομικό Κείμενο Απαρτίας (Ελληνικά)"
        greekValue={form.legal_text}
        onGreekChange={(v) => setForm({ ...form, legal_text: v })}
        greekRows={4}
        englishLabel="English Translation"
        englishValue={form.legal_text_en}
        onEnglishChange={(v) => setForm({ ...form, legal_text_en: v })}
        englishHelp="Used by the English Meeting PDF — no runtime auto-translation of the Greek version."
        englishRows={4}
        testidPrefix="legal-template"
        disabled={!isAdmin}
      />

      <MeetingTemplateSection
        title="Κείμενο Απόφασης Επιτροπής"
        greekLabel="Κείμενο Απόφασης Επιτροπής (Ελληνικά)"
        greekValue={form.committee_text}
        onGreekChange={(v) => setForm({ ...form, committee_text: v })}
        greekRows={3}
        englishLabel="English Translation"
        englishValue={form.committee_text_en}
        onEnglishChange={(v) => setForm({ ...form, committee_text_en: v })}
        englishRows={3}
        testidPrefix="committee-template"
        disabled={!isAdmin}
      />

      <MeetingTemplateSection
        title="Πρότυπο Επιλόγου"
        greekLabel="Πρότυπο Επιλόγου (Ελληνικά)"
        greekValue={form.epilogue_text_el}
        onGreekChange={(v) => setForm({ ...form, epilogue_text_el: v })}
        greekRows={8}
        greekMono
        englishLabel="English Translation"
        englishValue={form.epilogue_text_en}
        onEnglishChange={(v) => setForm({ ...form, epilogue_text_en: v })}
        englishHelp="Used when generating the English Meeting PDF for owners who don't read Greek."
        englishRows={8}
        englishMono
        testidPrefix="epilogue-template"
        disabled={!isAdmin}
      />

      {isAdmin && (
        <div className="flex justify-end pt-1">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="rounded-none bg-zinc-950"
            data-testid="save-meeting-templates-inline"
          >
            {saving ? 'Αποθήκευση…' : 'Αποθήκευση'}
          </Button>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 346 — Companies hub. Lists the 4 service-provider catalogues
// (Insurance / Elevator / Certification / Fire Extinguisher) and routes
// each row to its existing CRUD page so the user manages every supplier
// from one Settings entry. We deliberately re-use the existing dedicated
// pages instead of duplicating their CRUD UI: a single source of truth.
// ---------------------------------------------------------------------------
function CompaniesSection({ t }) {
  const navigate = useNavigate();

  const categories = [
    {
      id: 'insurance-companies',
      icon: Shield,
      label: t('insuranceCompanies') || 'Insurance Companies',
      desc: t('insuranceCompaniesHint') || 'Πάροχοι ασφάλειας πολυκατοικιών — στοιχεία επικοινωνίας & συμβόλαια.',
      path: '/insurance-companies',
      color: 'text-sky-700',
    },
    {
      id: 'elevator-companies',
      icon: Elevator,
      label: t('elevatorCompanies') || 'Elevator Companies',
      desc: t('elevatorCompaniesHint') || 'Εταιρείες συντήρησης ανελκυστήρων — στοιχεία επικοινωνίας & συμβόλαια.',
      path: '/elevator-companies',
      color: 'text-indigo-700',
    },
    {
      id: 'certification-companies',
      icon: Wrench,
      label: t('certificationCompanies') || 'Certification Companies',
      desc: t('certificationCompaniesHint') || 'Εταιρείες πιστοποίησης ανελκυστήρων — επιθεωρητές & συμβόλαια.',
      path: '/elevator-certification-companies',
      color: 'text-amber-700',
    },
    {
      id: 'fire-extinguisher-companies',
      icon: FireExtinguisher,
      label: t('fireExtinguisherCompanies') || 'Fire Extinguisher Companies',
      desc: t('fireExtinguisherCompaniesHint') || 'Εταιρείες πυροσβεστήρων — ετήσιες πιστοποιήσεις & συμβόλαια.',
      path: '/fire-extinguisher-companies',
      color: 'text-red-700',
    },
  ];

  return (
    <div className="space-y-3" data-testid="companies-section">
      <p className="text-xs text-zinc-500 leading-relaxed">
        {t('companiesIntro') ||
          'Διαχειριστείτε όλους τους παρόχους υπηρεσιών από ένα σημείο. Κάθε κατηγορία ανοίγει την οθόνη CRUD της με αναζήτηση, επεξεργασία και διαγραφή.'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="companies-grid">
        {categories.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => navigate(c.path)}
              className="bg-white border border-zinc-200 px-4 py-4 flex items-center gap-3 text-left hover:bg-zinc-50 hover:border-zinc-300 transition-colors group"
              data-testid={`companies-card-${c.id}`}
            >
              <div className="w-11 h-11 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                <Icon size={22} weight="fill" className={c.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-800">{c.label}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{c.desc}</p>
              </div>
              <CaretRight
                size={16}
                className="text-zinc-300 group-hover:text-zinc-600 transition-colors flex-shrink-0"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 352 — General Meetings hub. Two sub-categories:
//   1. Agenda Templates  — deep-link to /agenda-templates (existing page)
//   2. Meeting Text Templates  — inline reuse of MeetingTemplatesSection
// ---------------------------------------------------------------------------
function GeneralMeetingsSection({ t, isAdmin }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null); // null | 'meetingTemplates'

  if (sub === 'meetingTemplates') {
    return (
      <div className="space-y-3" data-testid="general-meetings-text-templates">
        <button
          type="button"
          onClick={() => setSub(null)}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-2"
          data-testid="general-meetings-sub-back"
        >
          <ArrowLeft size={14} />
          <span className="font-semibold">{t('generalMeetings') || 'General Meetings'}</span>
        </button>
        <MeetingTemplatesSection t={t} isAdmin={isAdmin} />
      </div>
    );
  }

  const cards = [
    {
      id: 'agenda-templates',
      icon: ListChecks,
      label: t('agendaTemplates') || 'Πρότυπα Ημερήσιας Διάταξης',
      desc: t('agendaTemplatesHint') || 'Πρότυπα θεμάτων που χρησιμοποιούνται όταν δημιουργείτε νέα γενική συνέλευση.',
      onClick: () => navigate('/agenda-templates'),
      color: 'text-indigo-700',
    },
    {
      id: 'meeting-text-templates',
      icon: Gavel,
      label: t('meetingTextTemplates') || 'Πρότυπα Κειμένων Συνελεύσεων',
      desc: t('meetingTextTemplatesHint') || 'Νομικό κείμενο απαρτίας & κείμενο απόφασης επιτροπής — αυτόματα κείμενα στα πρακτικά.',
      onClick: () => setSub('meetingTemplates'),
      color: 'text-amber-700',
    },
  ];

  return (
    <div className="space-y-3" data-testid="general-meetings-section">
      <p className="text-xs text-zinc-500 leading-relaxed">
        {t('generalMeetingsIntro') ||
          'Κεντρική διαχείριση των προτύπων που χρησιμοποιούνται σε όλες τις γενικές συνελεύσεις των πολυκατοικιών.'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="general-meetings-grid">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.id}
              type="button"
              onClick={c.onClick}
              className="bg-white border border-zinc-200 px-4 py-4 flex items-center gap-3 text-left hover:bg-zinc-50 hover:border-zinc-300 transition-colors group"
              data-testid={`general-meetings-card-${c.id}`}
            >
              <div className="w-11 h-11 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                <Icon size={22} weight="fill" className={c.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-800">{c.label}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{c.desc}</p>
              </div>
              <CaretRight
                size={16}
                className="text-zinc-300 group-hover:text-zinc-600 transition-colors flex-shrink-0"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
